import axios from "axios";

import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
/**
 * API endpoint configuration.
 * Each key maps to a specific API endpoint path.
 */
const API_ENDPOINTS = Object.freeze({
  LOGIN: '/api/method/login',
  LOGOUT: '/api/method/logout',
  GET_CURRENT_USER: '/api/method/orgain.user_api.get_user_details',
  GET_USER_BY_EMAIL: '/api/method/orgain.user_api.get_user_details',
  GET_ALL_USERS: '/api/method/orgain.user_api.get_user_data',
  GET_ALL_ROLES: '/api/method/orgain.user_api.get_orgain_roles',
  GET_ALLOWED_MODULES: '/api/method/orgain.user_api.get_user_role_modules',
  EDIT_USER: '/api/method/orgain.user_api.update_user_details',
  RESET_PASSWORD: "/api/method/orgain.user_api.reset_password",
  CREATE_USER: '/api/method/orgain.user_api.create_new_user',
  CHECK_USER_EXISTS: '/api/method/orgain.user_api.check_user_exists',
  FILE_UPLOAD: '/api/method/upload_file',
  DELETE_DOC: '/api/method/frappe.client.delete',
  GET_USER_ROLES: '/api/method/orgain.user_api.get_user_roles',
  DEACTIVATE_USER: '/api/method/orgain.user_api.suspend_user',
  CREATE_ROLE: '/api/method/orgain.user_api.create_role_with_modules',
  DELETE_USER: '/api/method/orgain.user_api.remove_user',
  UPDATE_USER_AVATAR: '/api/method/orgain.user_api.update_user_image',
  LOGOUT_ALL_DEVICE: '/api/method/orgain.user_api.logout_all_sessions ',
  UPDATE_ROLE_MODULES: '/api/method/orgain.user_api.update_role_modules',
  GET_CURRENT_USER_MODULES: '/api/method/orgain.user_api.get_current_user_accessible_modules',
  UPDATE_USER_PASSWORD: '/api/method/orgain.user_api.update_user_password',
  GET_JOB_ROLE_OPTIONS: '/api/method/orgain.user_api.get_job_role_options',

  // Landing-page KPIs (Postgres / Neo4j / Qdrant aggregate counts)
  GET_DASHBOARD_STATS: '/api/method/orgain.kb.api.chat_api.get_dashboard_stats',
  // Landing-page suggested queries generated from real KB metadata
  GET_SUGGESTED_PROMPTS: '/api/method/orgain.kb.api.chat_api.get_suggested_prompts',
  GET_TOP_SESSIONS: '/api/method/orgain.kb.api.chat_api.get_top_sessions',

  // KNOWLEDGE_BASE - Frappy ERP APIs
  GET_FOLDER_CONTENTS: '/api/method/orgain.kb.api.knowledge_base_api.get_folder_contents',
  CREATE_KNOWLEDGE_BASE_ITEM: '/api/method/orgain.kb.api.knowledge_base_api.create_item',
  UPDATE_KNOWLEDGE_BASE_ITEM: '/api/method/orgain.kb.api.knowledge_base_api.update_item',
  DELETE_KNOWLEDGE_BASE_ITEM: '/api/method/orgain.kb.api.knowledge_base_api.delete_item',
  DELETE_SUBCATEGORY_DOCUMENT: '/api/method/orgain.knowledge_base_api.delete_subcategory_document',
  SEARCH_KNOWLEDGE_BASE_ITEMS: '/api/method/orgain.kb.api.knowledge_base_api.search_items',
  GET_BREADCRUMB: '/api/method/orgain.kb.api.knowledge_base_api.get_breadcrumb',

  // CUSTOMER CRM - Frappy ERP APIs
  GET_CUSTOMERS_LIST: '/api/method/orgain.customer_api.get_customers_list',
  CREATE_CUSTOMER: '/api/method/orgain.customer_api.create_customer',
  GET_CUSTOMER_DETAILS_API: '/api/method/orgain.customer_api.get_customer_details',
  UPDATE_CUSTOMER: '/api/method/orgain.customer_api.update_customer',
  DELETE_CUSTOMER: '/api/method/orgain.customer_api.delete_customer',
  GET_TERRITORIES: '/api/method/orgain.customer_api.get_territories',
  GET_CUSTOMER_GROUPS: '/api/method/orgain.customer_api.get_customer_groups',

  // COMPANY BRANDING - Frappe ERP APIs
  GET_COMPANY_DETAILS: '/api/method/orgain.company_api.get_company_details',
  UPDATE_COMPANY_DETAILS: '/api/method/orgain.company_api.update_company_details',
  UPLOAD_COMPANY_LOGO: '/api/method/orgain.company_api.upload_company_logo',
  GET_DEFAULT_BRANDING: '/api/method/orgain.company_api.get_default_branding',

  // STREAMING MESSAGES - Frappe ERP APIs
  GET_STREAMING_MESSAGES: '/api/method/orgain.streaming_messages_api.get_streaming_messages',
  GET_STREAMING_MESSAGES_BY_CATEGORY: '/api/method/orgain.streaming_messages_api.get_messages_by_category',

  // GEMINI AI DESIGN - Frappe ERP APIs
  GET_PROPERTY_ANALYSIS: '/api/method/orgain.gemini_api.get_property_analysis',
  GENERATE_LANDSCAPE_DESIGN: '/api/method/orgain.gemini_api.generate_design',
  GENERATE_VIDEO_FROM_TEXT: '/api/method/orgain.gemini_api.generate_video_from_text',
  GENERATE_VIDEO_FROM_IMAGE: '/api/method/orgain.gemini_api.generate_video_from_image',
  SUBMIT_DESIGN_GENERATION: '/api/method/orgain.gemini_api.submit_design_generation',
  GET_VIDEO_STATUS: '/api/method/orgain.gemini_api.get_video_status',
  GET_ACTIVE_VIDEO_JOBS: '/api/method/orgain.gemini_api.get_active_jobs',
  CREATE_DESIGN_CHAT_SESSION: '/api/method/orgain.gemini_api.create_chat',
  DELETE_DESIGN_CHAT_SESSION: '/api/method/orgain.gemini_api.delete_chat_session',
  GET_DESIGN_CHAT_SESSION: '/api/method/orgain.gemini_api.get_chat_session',
  GET_DESIGN_CHAT_SESSIONS_LIST: '/api/method/orgain.gemini_api.get_chat_sessions_list',
  SEARCH_DESIGN_CHAT_SESSIONS: '/api/method/orgain.gemini_api.search_chat_sessions',
  GET_GROUPED_CHAT_SESSIONS_BY_DATE: '/api/method/orgain.gemini_api.get_grouped_chat_sessions_by_date',
  VALIDATE_SESSION_TITLE: '/api/method/orgain.gemini_api.validate_session_title',
  UPDATE_CHAT_MESSAGE: '/api/method/orgain.gemini_api.update_chat_message',
  UPDATE_CHAT_SESSION_TITLE: '/api/method/orgain.gemini_api.update_chat_session_title',
  GET_GEMINI_API_HEALTH: '/api/method/orgain.gemini_api.health',
  GET_MAX_PRODUCT_LIMIT: '/api/method/orgain.gemini_api.get_max_product_limit',
  CHAT_CONVERSATION: '/api/method/orgain.gemini_api.chat_conversation',
  // Design Q&A - LLM-based classification + response for design chat questions
  DESIGN_QA: '/api/method/orgain.llm_provider_api.design_qa',
  // General Chat with intelligent routing (web search, design redirect, knowledge base)
  GENERAL_CHAT: '/api/method/orgain.llm_provider_api.general_chat',
  // Real-time streaming chat endpoint (SSE) for ChatGPT-style word-by-word responses
  STREAM_CHAT: '/api/method/orgain.llm_provider_api.stream_chat',

  // KB DOCUMENT API - AI Document Analysis
  BULK_DELETE_UNAPPROVED_DOCUMENTS: '/api/method/orgain.kb.api.document_api.bulk_delete_unapproved_documents',
  BULK_DELETE_STATUS: '/api/method/orgain.kb.api.document_api.get_unapproved_bulk_delete_status',
  BULK_LINK_DOCUMENTS_TO_KB: '/api/method/orgain.kb.api.document_api.bulk_link_documents_to_knowledge_base',
  BULK_UNLINK_DOCUMENTS_FROM_KB: '/api/method/orgain.kb.api.document_api.bulk_unlink_documents_from_knowledge_base',
  // Workflow 1: Batch Upload
  CHECK_LLM_CONFIGURATION: '/api/method/orgain.kb.api.document_api.check_llm_configuration',
  PRE_VALIDATE_DOCUMENTS: '/api/method/orgain.kb.api.document_api.pre_validate_documents',
  BATCH_UPLOAD_DOCUMENTS: '/api/method/orgain.kb.api.document_api.batch_upload_documents',
  SAVE_CHAT_RESPONSE_TO_KB: '/api/method/orgain.kb.api.document_api.save_chat_response_to_kb',
  CHECK_DOCUMENT_TITLE_EXISTS: '/api/method/orgain.kb.api.document_api.check_document_title_exists',
  SUBMIT_CHAT_FEEDBACK: '/api/method/orgain.kb.api.chat_api.submit_chat_feedback',
  GET_SESSION_FEEDBACK: '/api/method/orgain.kb.api.chat_api.get_session_feedback',
  REMOVE_CHAT_FEEDBACK: '/api/method/orgain.kb.api.chat_api.remove_chat_feedback',

  // KB VALIDATION API - Document Validation
  GET_PENDING_VALIDATIONS: '/api/method/orgain.kb.api.validation_api.get_pending_validations',
  APPROVE_DOCUMENT: '/api/method/orgain.kb.api.validation_api.approve_document',
  REJECT_DOCUMENT: '/api/method/orgain.kb.api.validation_api.reject_document',
  GET_VALIDATION_STATS: '/api/method/orgain.kb.api.validation_api.get_validation_stats',
  GET_VALIDATION_DETAILS: '/api/method/orgain.kb.api.validation_api.get_validation_details',
  UPDATE_DOCUMENT_METADATA: '/api/method/orgain.kb.api.validation_api.update_document_metadata',
  BULK_APPROVE_DOCUMENTS: '/api/method/orgain.kb.api.validation_api.bulk_approve_documents',
  GET_QUEUE_SUMMARY: '/api/method/orgain.kb.api.validation_api.get_queue_summary',

  // KB EMBEDDING API - Document Embeddings
  GET_DOCUMENT_EMBEDDINGS: '/api/method/orgain.kb.api.embedding_api.get_document_embeddings',
  GET_EMBEDDING_STATISTICS: '/api/method/orgain.kb.api.embedding_api.get_embedding_statistics',
  GET_EMBEDDING_QUALITY: '/api/method/orgain.kb.api.embedding_api.get_embedding_quality',

  // KB GRAPH API - Knowledge Graph
  GET_DOCUMENT_GRAPH: '/api/method/orgain.kb.api.graph_api.get_document_graph',
  GET_GRAPH_STATISTICS: '/api/method/orgain.kb.api.graph_api.get_graph_statistics',

  // KB APPROVED DOCUMENTS API - Knowledge Base Documents
  LIST_APPROVED_DOCUMENTS: '/api/method/orgain.kb.api.approved_documents_api.list_approved_documents',
  LIST_CHAT_SPREADSHEETS: '/api/method/orgain.kb.api.approved_documents_api.list_chat_spreadsheets',
  GET_APPROVED_DOCUMENT_DETAIL: '/api/method/orgain.kb.api.approved_documents_api.get_approved_document_detail',
  DELETE_APPROVED_DOCUMENT: '/api/method/orgain.kb.api.approved_documents_api.delete_approved_document',
  DOWNLOAD_APPROVED_DOCUMENT: '/api/method/orgain.kb.api.approved_documents_api.download_approved_document',
  UPDATE_DOCUMENT_ENTITY_TYPE: '/api/method/orgain.kb.api.approved_documents_api.update_entity_type',
  BULK_UPDATE_ENTITY_TYPES: '/api/method/orgain.kb.api.approved_documents_api.bulk_update_entity_types',
  PREVIEW_BULK_ENTITY_UPDATE: '/api/method/orgain.kb.api.approved_documents_api.preview_bulk_entity_update',
  GET_UNIQUE_ENTITY_VALUES: '/api/method/orgain.kb.api.approved_documents_api.get_unique_entity_values',
  KB_UPDATE_ENTITY_VALUE: '/api/method/orgain.kb.api.approved_documents_api.update_entity_value_and_context',
  GET_ENTITY_RELATIONSHIPS: '/api/method/orgain.kb.api.approved_documents_api.get_entity_relationships',

  // KB ENTITY TYPE API - Entity Type Management
  GET_ENTITY_TYPES: '/api/method/orgain.kb.api.entity_type_api.get_entity_types',
  GET_ENTITY_TYPES_PAGE: '/api/method/orgain.kb.api.entity_type_api.get_entity_types_page',
  GET_ENTITY_TYPES_SUMMARY: '/api/method/orgain.kb.api.entity_type_api.get_entity_types_summary',
  GET_ENTITY_TYPE: '/api/method/orgain.kb.api.entity_type_api.get_entity_type',
  GET_ENTITY_TYPE_CATEGORIES: '/api/method/orgain.kb.api.entity_type_api.get_entity_type_categories',
  GET_ENTITY_TYPE_STATISTICS: '/api/method/orgain.kb.api.entity_type_api.get_entity_type_statistics',
  GET_NEO4J_ENTITY_USAGE: '/api/method/orgain.kb.api.entity_type_api.get_neo4j_entity_usage',
  CREATE_ENTITY_TYPE: '/api/method/orgain.kb.api.entity_type_api.create_entity_type',
  UPDATE_ENTITY_TYPE: '/api/method/orgain.kb.api.entity_type_api.update_entity_type',
  DELETE_ENTITY_TYPE: '/api/method/orgain.kb.api.entity_type_api.delete_entity_type',
  RELOAD_ENTITY_TYPE_REGISTRY: '/api/method/orgain.kb.api.entity_type_api.reload_entity_type_registry',

  // BRAIN SETTINGS API - Industry / Providers / API keys / Notifications
  GET_BRAIN_SETTINGS: '/api/method/orgain.kb.api.brain_settings_api.get_orgain_brain_settings',
  UPDATE_BRAIN_SETTINGS: '/api/method/orgain.kb.api.brain_settings_api.update_brain_settings',
  UPDATE_PROVIDER_API_KEY: '/api/method/orgain.kb.api.brain_settings_api.update_provider_api_key',
  TEST_PROVIDER_API_KEY: '/api/method/orgain.kb.api.brain_settings_api.test_provider_api_key',
  LIST_DOMAIN_KNOWLEDGE_TEMPLATES: '/api/method/orgain.kb.api.brain_settings_api.list_domain_knowledge_templates',
  LIST_LLM_MODELS: '/api/method/orgain.kb.api.brain_settings_api.list_llm_models',
  LIST_LLM_PROVIDERS: '/api/method/orgain.kb.api.brain_settings_api.list_llm_providers',
  SAVE_LLM_MODEL: '/api/method/orgain.kb.api.brain_settings_api.save_llm_model',
  DESCRIBE_ACTIVE_LLM_CONFIG: '/api/method/orgain.kb.api.brain_settings_api.describe_active_llm_config',
  // KB Settings - Email tab
  LIST_EMAIL_ACCOUNTS: '/api/method/orgain.kb.api.brain_settings_api.list_email_accounts',
  GET_EMAIL_ACCOUNT_STATUS: '/api/method/orgain.kb.api.brain_settings_api.get_email_account_status',
  GET_EMAIL_ACCOUNT: '/api/method/orgain.kb.api.brain_settings_api.get_email_account',
  SAVE_EMAIL_ACCOUNT: '/api/method/orgain.kb.api.brain_settings_api.save_email_account',
  TEST_EMAIL_ACCOUNT: '/api/method/orgain.kb.api.brain_settings_api.test_email_account',
  TEST_EMAIL_DRAFT: '/api/method/orgain.kb.api.brain_settings_api.test_email_draft',
  DELETE_EMAIL_ACCOUNT: '/api/method/orgain.kb.api.brain_settings_api.delete_email_account',
  // KB Settings - native in-app OAuth (Email Account). The Connected App is
  // provisioned in the Frappe backend; the SPA only reads the list to bind to it.
  LIST_CONNECTED_APPS: '/api/method/orgain.kb.api.brain_settings_api.list_connected_apps',
  AUTHORIZE_EMAIL_ACCOUNT: '/api/method/orgain.kb.api.brain_settings_api.authorize_email_account',
  ADOPT_EMAIL_OAUTH_IDENTITY: '/api/method/orgain.kb.api.email_oauth.adopt_email_oauth_identity',
  // KB ENTITY QA API - Entity Quality Assurance
  GET_ENTITY_QA_DASHBOARD: '/api/method/orgain.kb.api.entity_qa_api.get_qa_dashboard',
  GET_ENTITY_QA_METRICS: '/api/method/orgain.kb.api.entity_qa_api.get_qa_metrics',
  GET_ENTITY_QA_ISSUES: '/api/method/orgain.kb.api.entity_qa_api.get_pending_issues',
  GET_ENTITY_QA_DOCUMENT_ISSUES: '/api/method/orgain.kb.api.entity_qa_api.get_document_issues',
  RESOLVE_ENTITY_QA_ISSUE: '/api/method/orgain.kb.api.entity_qa_api.resolve_issue',
  BATCH_RESOLVE_ENTITY_QA_ISSUES: '/api/method/orgain.kb.api.entity_qa_api.batch_resolve_issues',
  GET_CANONICAL_ENTITIES: '/api/method/orgain.kb.api.entity_qa_api.get_canonical_entities',
  UPDATE_CANONICAL_ENTITY: '/api/method/orgain.kb.api.entity_qa_api.update_canonical_entity',
  DELETE_CANONICAL_ENTITY: '/api/method/orgain.kb.api.entity_qa_api.delete_canonical_entity',
  GET_ENTITY_QA_NEO4J_STATS: '/api/method/orgain.kb.api.entity_qa_api.get_neo4j_entity_stats',
  REBUILD_ENTITY_QA_NEO4J: '/api/method/orgain.kb.api.entity_qa_api.rebuild_neo4j_graph',
  GET_ENTITY_QA_CORRECTION_RULES: '/api/method/orgain.kb.api.entity_qa_api.get_correction_rules',
  BULK_DELETE_CANONICAL_ENTITIES: '/api/method/orgain.kb.api.entity_qa_api.bulk_delete_entities',
  BULK_RETYPE_ENTITIES: '/api/method/orgain.kb.api.entity_qa_api.bulk_retype_entities',
  AI_ANALYZE_ENTITIES: '/api/method/orgain.kb.api.entity_qa_api.ai_analyze_entities',
  AI_ANALYZE_AND_APPLY: '/api/method/orgain.kb.api.entity_qa_api.ai_analyze_and_apply_corrections',
  MERGE_CANONICAL_ENTITIES: '/api/method/orgain.kb.api.entity_qa_api.merge_entities',
  ADD_CORRECTION_RULE: '/api/method/orgain.kb.api.entity_qa_api.add_correction_rule',
  DELETE_QA_ISSUES: '/api/method/orgain.kb.api.entity_qa_api.delete_qa_issues',
  UPDATE_CORRECTION_RULE: '/api/method/orgain.kb.api.entity_qa_api.update_correction_rule',
  DELETE_CORRECTION_RULE: '/api/method/orgain.kb.api.entity_qa_api.delete_correction_rule',
  TOGGLE_CORRECTION_RULE: '/api/method/orgain.kb.api.entity_qa_api.toggle_correction_rule',
  REVIEW_DATA_QUALITY: '/api/method/orgain.kb.api.entity_qa_api.review_data_quality',
  BULK_DISMISS_ISSUES: '/api/method/orgain.kb.api.entity_qa_api.bulk_dismiss_issues',
  BULK_RESOLVE_ISSUES: '/api/method/orgain.kb.api.entity_qa_api.bulk_resolve_issues',
  GET_ISSUE_TYPE_BREAKDOWN: '/api/method/orgain.kb.api.entity_qa_api.get_issue_type_breakdown',
  AUTO_FIX_ISSUES: '/api/method/orgain.kb.api.entity_qa_api.auto_fix_issues',
  AUTO_FIX_BY_TYPE: '/api/method/orgain.kb.api.entity_qa_api.auto_fix_by_type',
  RESOLVE_DUPLICATE_ISSUE: '/api/method/orgain.kb.api.entity_qa_api.resolve_duplicate_issue',
  AUDIT_VECTOR_HEALTH: '/api/method/orgain.kb.api.entity_qa_api.audit_vector_health',
  GET_STALE_DOCUMENTS: '/api/method/orgain.kb.api.entity_qa_api.get_stale_documents',
  BATCH_REINDEX_DOCUMENTS: '/api/method/orgain.kb.api.entity_qa_api.batch_reindex_documents',
  GET_VALID_ENTITY_TYPES: '/api/method/orgain.kb.api.entity_qa_api.get_valid_entity_types',
  GET_DUPLICATE_CLUSTERS: '/api/method/orgain.kb.api.entity_qa_api.get_duplicate_clusters',
  MERGE_CLUSTER: '/api/method/orgain.kb.api.entity_qa_api.merge_cluster',
  GET_SELF_LEARNING_SETTINGS: '/api/method/orgain.kb.api.entity_qa_api.get_self_learning_settings',
  UPDATE_SELF_LEARNING_SETTINGS: '/api/method/orgain.kb.api.entity_qa_api.update_self_learning_settings',

  // KB CHAT API - Knowledge Base Chat
  STREAM_CHAT_MESSAGE: '/api/method/orgain.kb.api.chat_api.stream_chat_message',
  SEND_CHAT_MESSAGE: '/api/method/orgain.kb.api.chat_api.send_chat_message',

  // KB ADMIN API - System Health
  HEALTH_CHECK: '/api/method/orgain.kb.api.admin_api.health_check',

  // KB VIDEO API - Video Processing
  // KB_VIDEO_UPLOAD: '/api/method/orgain.kb.api.video_api.upload_video',
  // KB_VIDEO_TASK_STATUS: '/api/method/orgain.kb.api.video_api.get_video_task_status',
  // KB_VIDEO_HEALTH: '/api/method/orgain.kb.api.video_api.video_health',

  // KB AUDIO API - Audio Processing
  // KB_AUDIO_UPLOAD: '/api/method/orgain.kb.api.audio_api.upload_audio',
  // KB_AUDIO_TASK_STATUS: '/api/method/orgain.kb.api.audio_api.get_audio_task_status',
  // KB_AUDIO_HEALTH: '/api/method/orgain.kb.api.audio_api.audio_health',

  // IONOS S3 STORAGE - URL Generation & Image Fetching
  GENERATE_URL_FROM_EXISTING: '/api/method/orgain.ionos_s3_storage.api_generate_url_from_existing',
  FETCH_IMAGE_AS_BASE64: '/api/method/orgain.ionos_s3_storage.api_fetch_image_as_base64',
  DOWNLOAD_S3_IMAGE: '/api/method/orgain.ionos_s3_storage.api_download_s3_image',
  REFRESH_S3_URLS: '/api/method/orgain.s3_url_refresh.api_refresh_s3_urls',

  // KB CLOUD SOURCE API - SharePoint / Google Drive imports
  CLOUD_GET_PROVIDER_AVAILABILITY: '/api/method/orgain.kb.api.cloud_source_api.get_provider_availability',
  CLOUD_HAS_CONNECTED_SOURCE: '/api/method/orgain.kb.api.cloud_source_api.has_connected_source',
  GET_SETUP_STATUS: '/api/method/orgain.company_api.get_setup_status',
  DANGER_ZONE_STATUS: '/api/method/orgain.wipe_corpus.danger_zone_status',
  DANGER_ZONE_CLEAR_CHATS: '/api/method/orgain.wipe_corpus.clear_all_chats',
  DANGER_ZONE_WIPE_DATA: '/api/method/orgain.wipe_corpus.wipe',
  LIST_FAILED_DOCUMENTS: '/api/method/orgain.kb.api.validation_api.list_failed_documents',
  DELETE_FAILED_DOCUMENT: '/api/method/orgain.kb.api.validation_api.delete_failed_document',
  CLOUD_LIST_MY_SOURCES: '/api/method/orgain.kb.api.cloud_source_api.list_my_sources',
  CLOUD_GET_SOURCE: '/api/method/orgain.kb.api.cloud_source_api.get_source',
  CLOUD_SAVE_SOURCE: '/api/method/orgain.kb.api.cloud_source_api.save_source',
  CLOUD_DELETE_SOURCE: '/api/method/orgain.kb.api.cloud_source_api.delete_source',
  CLOUD_START_OAUTH: '/api/method/orgain.kb.api.cloud_source_api.start_oauth',
  CLOUD_CHECK_OAUTH_STATUS: '/api/method/orgain.kb.api.cloud_source_api.check_oauth_status',
  CLOUD_DISCONNECT: '/api/method/orgain.kb.api.cloud_source_api.disconnect',
  CLOUD_RECONNECT: '/api/method/orgain.kb.api.cloud_source_api.reconnect',
  CLOUD_CANCEL_PENDING_OAUTH: '/api/method/orgain.kb.api.cloud_source_api.cancel_pending_oauth',
  CLOUD_TEST_CONNECTION: '/api/method/orgain.kb.api.cloud_source_api.test_connection',
  CLOUD_LIST_SHAREPOINT_SITES: '/api/method/orgain.kb.api.cloud_source_api.list_sharepoint_sites',
  CLOUD_LIST_FOLDERS: '/api/method/orgain.kb.api.cloud_source_api.list_folders',
  CLOUD_TRIGGER_SYNC: '/api/method/orgain.kb.api.cloud_source_api.trigger_sync',
  CLOUD_TRIGGER_DISCOVERY_ONLY: '/api/method/orgain.kb.api.cloud_source_api.trigger_discovery_only',
  CLOUD_GET_SYNC_PROGRESS: '/api/method/orgain.kb.api.cloud_source_api.get_sync_progress',
  CLOUD_RETRY_FAILED: '/api/method/orgain.kb.api.cloud_source_api.retry_failed',
  CLOUD_STOP_SYNC: '/api/method/orgain.kb.api.cloud_source_api.stop_sync',
  CLOUD_LIST_SYNC_HISTORY: '/api/method/orgain.kb.api.cloud_source_api.list_sync_history',
  CLOUD_LIST_FILES: '/api/method/orgain.kb.api.cloud_source_api.list_files',
  CLOUD_ESTIMATE_SCOPE: '/api/method/orgain.kb.api.cloud_source_api.estimate_scope',
  CLOUD_RECOMPUTE_AGGREGATES: '/api/method/orgain.kb.api.cloud_source_api.recompute_aggregates',

  // KB SETTINGS API - Settings Management
  KB_SETTINGS_CATEGORIES: '/api/method/orgain.kb.api.admin_api.get_categories',
  KB_SETTINGS_DEPARTMENTS: '/api/method/orgain.kb.api.admin_api.get_departments',
  KB_SETTINGS_LANGUAGES: '/api/method/orgain.kb.api.admin_api.get_languages',
  KB_SETTINGS_DOCUMENT_TYPES: '/api/method/orgain.kb.api.admin_api.get_document_types',
  GET_ACTIVE_DOCUMENT_TYPES: '/api/method/orgain.kb.api.document_api.get_active_document_types',
  KB_SETTINGS_ACCESS_LEVELS: '/api/method/orgain.kb.api.admin_api.get_access_levels',
  KB_SETTINGS_SUMMARY: '/api/method/orgain.kb.api.admin_api.get_settings_summary',
});

/**
 * Base URL of the API server.
 * In development mode, we use empty string to leverage Vite's proxy.
 * In production, we use the full URL.
 */
const isDevelopment = window.location.hostname === 'localhost' ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost');

// In development, use relative URLs so Vite proxy handles the requests
// In production, use the full ERPNext URL
const BASE_URL = isDevelopment ? '' : ('');

/**
 * Returns the full API endpoint URL based on a provided key.
 *
 * @param {keyof typeof API_ENDPOINTS | string} key - The key representing the API endpoint.
 * @returns {string | null} - The full API endpoint URL, or null if the key is not found.
 *
 * @example
 * getApiEndpoint("LOGIN");
 * // In dev: "/api/method/login" (uses proxy)
 * // In prod: "https://api.example.com/api/method/login"
 *
 * getApiEndpoint("INVALID_KEY");
 * // null
 */
function getApiEndpoint(key) {
  if (!key || typeof key !== "string") {
    logger.warn("Invalid key provided to getApiEndpoint.");
    return null;
  }

  const normalizedKey = key.toUpperCase(); // ensure case-insensitivity
  const endpoint = API_ENDPOINTS[normalizedKey];

  if (!endpoint) {
    logger.warn(`No API endpoint found for key: ${key}`);
    return null;
  }

  return `${BASE_URL}${endpoint}`;
}


/**
 * Get full image URL from ERPNext file path
 *
 * Handles various path formats:
 * - Full URLs (http/https) - returned as-is
 * - ERPNext file paths (/files/...) - proxied in dev, full URL in prod
 * - Relative paths without leading slash - normalized and processed
 *
 * @param {string | null | undefined} path - The image path from ERPNext
 * @returns {string | null} - The full image URL or null if invalid
 */
function resolveImagePath(path: string | null | undefined): string | null {
  // Handle null, undefined, or non-string values
  if (!path || typeof path !== "string") {
    return null;
  }

  // Trim whitespace
  const trimmedPath = path.trim();

  // Return null for empty strings
  if (trimmedPath === '') {
    return null;
  }

  // Handle malformed protocols (missing colon after scheme)
  // Backend may return URLs like "https//..." instead of "https://..."
  if (trimmedPath.startsWith('https//')) {
    return trimmedPath.replace('https//', 'https://');
  }
  if (trimmedPath.startsWith('http//')) {
    return trimmedPath.replace('http//', 'http://');
  }

  // If it's already a full URL (starts with http/https), return as-is
  if (trimmedPath.startsWith('http://') || trimmedPath.startsWith('https://')) {
    return trimmedPath;
  }

  // If it's a data URL (base64), return as-is
  if (trimmedPath.startsWith('data:')) {
    return trimmedPath;
  }

  // Get the production base URL for checking duplicates
  const prodBaseUrl = '';

  // If the path already contains the base URL, return it as-is to avoid duplication
  if (prodBaseUrl && trimmedPath.includes(prodBaseUrl)) {
    return trimmedPath;
  }

  // Normalize the path - ensure it starts with /
  let normalizedPath = trimmedPath;
  if (!normalizedPath.startsWith('/')) {
    normalizedPath = '/' + normalizedPath;
  }

  // In development mode, return relative path to use Vite proxy
  // This avoids CORS issues when fetching images
  if (isDevelopment && (normalizedPath.startsWith('/files/') || normalizedPath.startsWith('/private/files/'))) {
    // Use relative path - Vite will proxy to ERPNext server
    return normalizedPath;
  }

  // In production, prepend the base URL
  return `${prodBaseUrl}${normalizedPath}`;
}

/**
 * Resolve a Frappe file path to a usable URL.
 *
 * `version` appends a cache-busting query param (typically the owning record's
 * `modified` timestamp). Pass it ONLY for images that can change in place -
 * e.g. a user avatar, where a re-upload may reuse the same `/files/...` path:
 * without the buster the URL string is identical, so React never changes the
 * <img src> and the browser keeps painting the stale cached bytes until a hard
 * reload. Omit it for immutable/content-addressed assets so normal HTTP
 * caching still applies. Never appended to data: URLs (would corrupt them).
 */
function getImageURL(
  path: string | null | undefined,
  version?: string | number | null,
): string | null {
  const resolved = resolveImagePath(path);
  if (!resolved || version === undefined || version === null || version === '' || resolved.startsWith('data:')) {
    return resolved;
  }
  const separator = resolved.includes('?') ? '&' : '?';
  return `${resolved}${separator}v=${encodeURIComponent(String(version))}`;
}


interface ResetPasswordResult {
  success: boolean;
  message?: string;
  emailSent?: boolean;
  error?: Error;
}

// reset pwd ( send pwd reset mail with token )
async function resetPassword(email: string): Promise<ResetPasswordResult> {
  try {
    const { data } = await axios.post(
      getApiEndpoint("RESET_PASSWORD"),
      { user: email },
      { withCredentials: true, headers: { "Content-Type": "application/json" } },
    );
    const res = data?.message;
    if (res?.status === "error") {
      return { success: false, emailSent: false, error: new Error(res.message) };
    }
    return { success: true, message: res?.message, emailSent: res?.email_sent };
  } catch (err) {
    return {
      success: false,
      error: new Error(
        getFriendlyErrorMessage(err, "Server error while resetting password."),
      ),
    };
  }
}



/**
 * Upload a file to the server.
 *
 * @param file - The file to be uploaded
 * @returns A promise resolving with the uploaded file URL (if successful)
 */
async function fileUpload(
  file: File,
  maxSizeMB = 5,
  options?: {
    folderToBeUploaded?: string;
    isPrivate?: boolean;
    uploadedBy?: string;
  }
): Promise<{ url?: string; error?: string }> {
  try {
    // Step 1: Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const errorMsg = `File size exceeds ${maxSizeMB} MB limit.`;
      logger.error(errorMsg);
      return { error: errorMsg };
    }

    // Step 2: Prepare FormData
    const formData = new FormData();
    formData.append("file", file);
    if (options?.folderToBeUploaded) {
      formData.append("folder_to_be_uploaded", options.folderToBeUploaded);
    }
    if (options?.isPrivate) {
      formData.append("is_private", "1");
    }
    if (options?.uploadedBy) {
      formData.append("uploaded_by", options.uploadedBy);
    }

    // Step 3: Upload file
    const uploadRes = await axios.post(
      getApiEndpoint("FILE_UPLOAD"),
      formData,
      {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Step 4: Extract file URL
    const fileUrl: string | undefined = uploadRes.data?.message?.file_url;
    return { url: fileUrl };
  } catch (error: unknown) {
    // Handle Axios vs Generic errors separately
    let errorMsg = "File upload failed.";
    if (axios.isAxiosError(error)) {
      // 413 Request Entity Too Large is raised by the web server / Frappe
      const status = error.response?.status;
      const is413 =
        status === 413 ||
        /413|request entity too large|exceeds the capacity/i.test(
          String(error.message),
        );
      if (is413) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
        errorMsg = `"${file.name}" (${fileSizeMB} MB) exceeds the server's upload size limit and was rejected before it finished uploading. Please upload a smaller file, or ask an administrator to raise the server limit.`;
      } else {
        errorMsg = error.response?.data?.message || error.message;
      }
    } else if (error instanceof Error) {
      errorMsg = error.message;
    }

    logger.error("File upload error:", errorMsg);
    return { error: errorMsg };
  }
}


function generateColor(): string {
  // Generate a random number between 0 and 16777215 (0xFFFFFF)
  const randomNum = Math.floor(Math.random() * 16777215);

  // Convert to hex and pad with leading zeros if needed
  const hexColor = "#" + randomNum.toString(16).padStart(6, "0").toUpperCase();

  return hexColor;
}


interface RoleModule {
  id: string;
  role_id: string;
  module_id: string;
  granted: boolean;
  created_at: string;
  updated_at: string;
  description: string
}

function formatAllowedModules(allowedModules: any[]): RoleModule[] {
  const now = new Date().toISOString();

  return allowedModules.flatMap((roleEntry) =>
    roleEntry.modules.flatMap((mod: any) => {

      const base: RoleModule[] = [
        {
          id: mod.module,
          role_id: roleEntry.role,
          module_id: mod.module,
          description: mod.description,
          granted: mod.show === 1,
          created_at: now,
          updated_at: now,
        },
      ];

      const subs =
        mod.sub_modules?.map((sub: any) => ({
          id: sub.module,
          role_id: roleEntry.role,
          description: sub.description,
          module_id: sub.module,
          granted: sub.show === 1,
          created_at: now,
          updated_at: now,
        })) ?? [];

      return [...base, ...subs];
    })
  );
}


/**
 * Convert nested roles/modules JSON into Module[] format
 * @param {Array} data - The roles + modules JSON
 * @returns {Module[]} - Flattened module list
 */
function convertToModules(data) {
  const now = new Date().toISOString();
  const modulesMap = new Map(); // to avoid duplicates by name

  data.forEach(roleEntry => {
    roleEntry.modules.forEach(mod => {
      // Add parent module
      if (!modulesMap.has(mod.module)) {
        modulesMap.set(mod.module, {
          id: mod.module,
          name: mod.module,
          description: mod.description || "", // optional, can be filled later
          category: "Parent", // label all parent modules
          is_active: mod.show === 1,
          created_at: now,
          updated_at: now,
        });
      }

      // Add sub-modules
      if (mod.sub_modules && mod.sub_modules.length > 0) {
        mod.sub_modules.forEach(sub => {
          if (!modulesMap.has(sub.module)) {
            modulesMap.set(sub.module, {
              id: sub.module,
              name: sub.module,
              description: sub.description || "",
              category: mod.module, // parent module name as category
              is_active: sub.show === 1,
              created_at: now,
              updated_at: now,
            });
          }
        });
      }
    });
  });

  return Array.from(modulesMap.values());
}



/**
 * Logout all devices/sessions for a user and redirect to login page
 *
 * @param email - The email of the user to logout from all devices
 * @returns Promise<{ success: boolean; error?: string }>
 */
async function logoutAllDevices(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await axios.post(getApiEndpoint("LOGOUT_ALL_DEVICE"),
      {
        user: email
      }
    );

    if (response.status === 200) {
      // Clear any local storage or session storage if needed
      localStorage.clear();
      sessionStorage.clear();

      return { success: true };
    } else {
      throw new Error("Failed to logout from all devices");
    }
  } catch (error: unknown) {
    let errorMsg = "Failed to logout from all devices.";

    if (axios.isAxiosError(error)) {
      errorMsg = error.response?.data?.message || error.message;
    } else if (error instanceof Error) {
      errorMsg = error.message;
    }

    logger.error("Logout all devices error:", errorMsg);
    return { success: false, error: errorMsg };
  }
}

/**
 * Update user password
 *
 * @param user - The email or user ID of the user to update password for
 * @param oldPassword - The current password of the user
 * @param newPassword - The new password to set
 * @param logoutAllSessions - Flag to specify whether to log the user out of all active sessions (default: false)
 * @returns Promise<{ success: boolean; error?: string }>
 */
async function updateUserPassword(
  user: string,
  oldPassword: string,
  newPassword: string,
  logoutAllSessions: boolean = false
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await axios.post(
      getApiEndpoint("UPDATE_USER_PASSWORD"),
      {
        user: user,
        old_password: oldPassword,
        new_password: newPassword,
        logout_all_sessions: logoutAllSessions
      },
      {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    // Check if response contains error in message
    if (response.data?.message?.status === "error" || response.data?.message?.status === "failed") {
      throw new Error(response.data.message.message || "Failed to update password");
    }

    // Check for success status
    if (response.status === 200 && response.data?.message?.status === "success") {
      return { success: true };
    }

    // If status is 200 but no explicit success status, treat as success (backward compatibility)
    if (response.status === 200 && !response.data?.message?.status) {
      return { success: true };
    }

    throw new Error("Failed to update password");
  } catch (error: unknown) {
    let errorMsg = "Failed to update password.";

    if (axios.isAxiosError(error)) {
      // Check if error response has the message structure
      if (error.response?.data?.message?.message) {
        errorMsg = error.response.data.message.message;
      } else if (error.response?.data?.message) {
        errorMsg = typeof error.response.data.message === 'string'
          ? error.response.data.message
          : error.response.data.message.message || error.message;
      } else {
        errorMsg = error.message;
      }
    } else if (error instanceof Error) {
      errorMsg = error.message;
    }

    logger.error("Update password error:", errorMsg);
    return { success: false, error: errorMsg };
  }
}

export { getApiEndpoint, API_ENDPOINTS, getImageURL, resetPassword, fileUpload, generateColor, formatAllowedModules, convertToModules, logoutAllDevices, updateUserPassword };
